import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import periodIcon from '~/assets/svg/audit/calendar-period.svg';
import Container from '~/components/container';
import {
  ACTIVITY_LIMITS,
  formatDateTime,
  formatPeriodEnd,
  hourlyPoints,
  MAX_HOURLY_POINTS,
  screenLabel,
  toActivityQuery,
  toChartPoints,
  useAccessControlActivity,
  userName,
  useUserSearch,
} from './api';
import ApiServices from './components/ApiServices';
import AppMarkers from './components/AppMarkers';
import AuditFilters from './components/AuditFilters';
import {
  EndpointsTable,
  RecentVisitsTable,
  UserActivityTable,
} from './components/AuditTables';
import OverviewCards from './components/OverviewCards';
import TopScreens from './components/TopScreens';
import VisitsChart from './components/VisitsChart';
import { MARKER_OPTIONS, SCREEN_OPTIONS, SERVICE_OPTIONS } from './mocks';
import {
  ActivityResources,
  AUDIT_ALL,
  AuditChartGranularity,
  AuditChartMetric,
  AuditFilters as Filters,
  AuditOption,
  AuditPeriodShortcut,
  SerieTemporalResponse,
} from './models';
import * as St from './styles';

const DATE_FORMAT = 'DD/MM/YYYY';

const PERIOD_DAYS: Record<AuditPeriodShortcut, number> = {
  '24H': 1,
  '3D': 3,
  '7D': 7,
  '30D': 30,
};

const DEFAULT_PERIOD: AuditPeriodShortcut = '24H';

const periodDates = (period: AuditPeriodShortcut) => ({
  dataInicial: moment()
    .subtract(PERIOD_DAYS[period], 'days')
    .format(DATE_FORMAT),
  dataFinal: moment().format(DATE_FORMAT),
});

const initialFilters = (): Filters => ({
  ...periodDates(DEFAULT_PERIOD),
  tela: AUDIT_ALL,
  servico: AUDIT_ALL,
  usuario: AUDIT_ALL,
  marcador: AUDIT_ALL,
});

// Recursos dos cards: cada um tem o próprio loading e erro e aparece assim
// que a sua chamada volta. A série temporal fica à parte (muda com o agrupamento).
const CARD_RESOURCES = [
  'visao-geral',
  'telas',
  'servicos',
  'marcadores',
  'endpoints',
  'usuarios',
  'visitas',
] as const;

type CardResource = typeof CARD_RESOURCES[number];

const CARD_PARAMS: { [R in CardResource]: ActivityResources[R]['params'] } = {
  'visao-geral': {},
  telas: { ordenarPor: 'VISITAS', limite: ACTIVITY_LIMITS.telas },
  servicos: { limite: ACTIVITY_LIMITS.servicos },
  marcadores: {},
  endpoints: { limite: ACTIVITY_LIMITS.endpoints },
  usuarios: { limite: ACTIVITY_LIMITS.usuarios },
  visitas: { limite: ACTIVITY_LIMITS.visitas },
};

interface CardState<R extends CardResource> {
  data?: ActivityResources[R]['response'];
  loading: boolean;
  error?: string;
}

type Cards = { [R in CardResource]: CardState<R> };

const initialCards = (): Cards =>
  CARD_RESOURCES.reduce(
    (acc, resource) => ({ ...acc, [resource]: { loading: true } }),
    {} as Cards,
  );

// Junta opções novas às já vistas, sem repetir, mantendo "Todos" no topo.
const mergeOptions = (
  current: AuditOption[],
  incoming: AuditOption[],
): AuditOption[] => {
  const ids = new Set(current.map((option) => option.id));
  return [...current, ...incoming.filter((option) => !ids.has(option.id))];
};

const Audit: React.FC = () => {
  const [form, setForm] = useState<Filters>(initialFilters);
  const [applied, setApplied] = useState<Filters>(form);
  const [period, setPeriod] = useState<AuditPeriodShortcut | undefined>(
    DEFAULT_PERIOD,
  );
  const [cards, setCards] = useState<Cards>(initialCards);

  const [metric, setMetric] = useState<AuditChartMetric>('VISITAS');
  const [granularity, setGranularity] = useState<AuditChartGranularity>('HORA');
  const [serie, setSerie] = useState<SerieTemporalResponse>();
  const [serieLoading, setSerieLoading] = useState(true);
  const [serieError, setSerieError] = useState<string>();

  // Telas e serviços são fixos (mocks.ts). Usuários: os primeiros 50 do
  // ms-usuario, os do card de atividade por usuário (acumulados entre
  // consultas) e a busca por nome.
  const [userOptions, setUserOptions] = useState<AuditOption[]>([
    { id: AUDIT_ALL, value: 'Todos os usuários' },
  ]);

  const { fetchResource } = useAccessControlActivity();
  const { searchUsers } = useUserSearch();

  const hourlyAllowed = hourlyPoints(applied) <= MAX_HOURLY_POINTS;

  useEffect(() => {
    let active = true;
    // Sem a lista inicial o select ainda funciona pela busca por nome.
    searchUsers()
      .then((options) => {
        if (active) setUserOptions((prev) => mergeOptions(prev, options));
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    let active = true;
    const query = toActivityQuery(applied);
    const setCard = <R extends CardResource>(
      resource: R,
      state: CardState<R>,
    ) => setCards((prev) => ({ ...prev, [resource]: state }));

    const load = <R extends CardResource>(resource: R) => {
      // O dado anterior fica guardado, mas o card mostra o loading.
      setCards((prev) => ({
        ...prev,
        [resource]: { ...prev[resource], loading: true },
      }));
      fetchResource(resource, query, CARD_PARAMS[resource])
        .then((data) => {
          if (!active) return;
          setCard(resource, { data, loading: false });
          if (resource === 'usuarios') {
            const {
              usuarios,
            } = data as ActivityResources['usuarios']['response'];
            setUserOptions((prev) =>
              mergeOptions(
                prev,
                usuarios.map(({ id, nome }) => ({
                  id,
                  // No select não há id embaixo: sem nome, ele identifica a opção.
                  value: nome?.trim() || `${userName(nome)} (${id})`,
                })),
              ),
            );
          }
        })
        .catch((err) => {
          if (active) {
            setCard(resource, {
              loading: false,
              error: err?.message ?? 'Erro ao consultar',
            });
          }
        });
    };

    CARD_RESOURCES.forEach(load);

    return () => {
      active = false;
    };
  }, [applied]); // eslint-disable-line react-hooks/exhaustive-deps

  // Visitas/Chamadas vêm no mesmo ponto; só o agrupamento busca de novo.
  useEffect(() => {
    let active = true;
    setSerieLoading(true);
    setSerieError(undefined);
    fetchResource('serie-temporal', toActivityQuery(applied), {
      agrupamento: granularity,
    })
      .then((data) => active && setSerie(data))
      .catch((err) => {
        if (!active) return;
        setSerie(undefined);
        setSerieError(err.message);
      })
      .finally(() => active && setSerieLoading(false));
    return () => {
      active = false;
    };
  }, [applied, granularity]); // eslint-disable-line react-hooks/exhaustive-deps

  const chart = useMemo(
    () => (serie ? toChartPoints(serie.pontos, metric, granularity) : []),
    [serie, metric, granularity],
  );

  const applyFilters = (filters: Filters) => {
    if (hourlyPoints(filters) > MAX_HOURLY_POINTS) setGranularity('DIA');
    // Objeto novo: "Consultar" com os mesmos filtros também recarrega os cards.
    setApplied({ ...filters });
  };

  const handleFormChange = (next: Filters) => {
    if (
      next.dataInicial !== form.dataInicial ||
      next.dataFinal !== form.dataFinal
    ) {
      setPeriod(undefined);
    }
    setForm(next);
  };

  const handlePeriodChange = (next: AuditPeriodShortcut) => {
    setPeriod(next);
    setForm({ ...form, ...periodDates(next) });
  };

  const handleClear = () => {
    const filters = initialFilters();
    setForm(filters);
    setPeriod(DEFAULT_PERIOD);
    applyFilters(filters);
  };

  const {
    'visao-geral': visaoGeral,
    telas,
    servicos,
    marcadores,
    endpoints,
    usuarios,
    visitas,
  } = cards;
  const loading =
    serieLoading || CARD_RESOURCES.some((resource) => cards[resource].loading);

  return (
    <Container>
      <St.Page>
        <St.Header>
          <h1>Atividades da plataforma</h1>
          <p>
            Entenda quais telas do backoffice são visitadas e quais chamadas de
            API cada visita gera.
          </p>
        </St.Header>

        <AuditFilters
          form={form}
          period={period}
          screens={SCREEN_OPTIONS}
          services={SERVICE_OPTIONS}
          users={userOptions}
          markers={MARKER_OPTIONS}
          loading={loading}
          onSearchUsers={searchUsers}
          onChange={handleFormChange}
          onPeriodChange={handlePeriodChange}
          onClear={handleClear}
          onSubmit={() => applyFilters(form)}
        />

        <St.SectionTitle>
          <h2>Visão geral</h2>
          {visaoGeral.data && !visaoGeral.loading && (
            <St.PeriodChip>
              <img src={periodIcon} alt="" />
              {formatDateTime(visaoGeral.data.periodoInicio)} -{' '}
              {formatPeriodEnd(visaoGeral.data.periodoFim)}
            </St.PeriodChip>
          )}
        </St.SectionTitle>

        <OverviewCards
          loading={visaoGeral.loading}
          error={visaoGeral.error}
          data={
            visaoGeral.data && {
              periodo: {
                inicio: formatDateTime(visaoGeral.data.periodoInicio),
                fim: formatPeriodEnd(visaoGeral.data.periodoFim),
              },
              visitas: visaoGeral.data.visitas,
              telasVisitadas: visaoGeral.data.telasVisitadas,
              totalTelas: visaoGeral.data.totalTelas,
              chamadasApi: visaoGeral.data.totalChamadas,
              chamadasPorVisita: visaoGeral.data.mediaChamadasPorVisita,
              usuarios: visaoGeral.data.usuarios,
              servicosApi: visaoGeral.data.servicos,
              visitasSemUsuario: visaoGeral.data.visitasSemUsuario,
            }
          }
        />

        <St.ChartRow>
          <VisitsChart
            points={chart}
            metric={metric}
            granularity={granularity}
            hourlyDisabled={!hourlyAllowed}
            loading={serieLoading}
            errorMessage={serieError}
            onMetricChange={setMetric}
            onGranularityChange={setGranularity}
          />
          <TopScreens
            loading={telas.loading}
            error={telas.error}
            screens={telas.data?.telas.map((tela) => ({
              tela: screenLabel(tela.correlationRoute),
              rota: tela.correlationRoute,
              visitas: tela.visitas,
              chamadasApi: tela.chamadas,
              chamadasPorVisita: tela.mediaChamadasPorVisita,
              usuarios: tela.usuarios,
            }))}
          />
        </St.ChartRow>

        <ApiServices
          loading={servicos.loading}
          error={servicos.error}
          services={servicos.data?.servicos.map((item) => ({
            servico: item.service,
            chamadas: item.chamadas,
            percentual: item.percentual,
          }))}
        />

        <AppMarkers
          loading={marcadores.loading}
          error={marcadores.error}
          markers={marcadores.data?.marcadores.map((item) => ({
            marcador: item.marcador,
            total: item.chamadas,
            percentual: item.percentual,
          }))}
        />

        <St.TablesRow>
          <EndpointsTable
            loading={endpoints.loading}
            error={endpoints.error}
            endpoints={endpoints.data?.endpoints.map((item) => ({
              endpoint: item.path,
              servico: item.service,
              metodo: item.method,
              chamadas: item.chamadas,
              usuarios: item.usuarios,
            }))}
          />
          <UserActivityTable
            loading={usuarios.loading}
            error={usuarios.error}
            users={usuarios.data?.usuarios.map((item) => ({
              usuario: userName(item.nome),
              idUsuario: item.id,
              visitas: item.visitas,
              chamadas: item.chamadas,
              servicos: item.servicos,
            }))}
          />
        </St.TablesRow>

        <RecentVisitsTable
          loading={visitas.loading}
          error={visitas.error}
          visits={visitas.data?.visitas.map((item) => ({
            correlationId: item.correlationId,
            dataHora: formatDateTime(item.inicio),
            usuario: item.usuarioId
              ? userName(item.usuarioNome)
              : 'Sem usuário identificado',
            idUsuario: item.usuarioId ?? '-',
            tela: screenLabel(item.correlationRoute),
            rota: item.correlationRoute,
            apiPath: item.apiPath,
            chamadas: item.chamadas,
          }))}
        />
      </St.Page>
    </Container>
  );
};

export default Audit;
